import { useMemo, type FC } from 'react'
import clsx from 'clsx'

import { Separator } from '@wartek-id/separator'
import { Badge } from '@wartek-id/badge'
import { Text } from '@wartek-id/text-v1'

import EvaluationResult from 'app/KinerjaKSPage/components/EvaluationResultV2'
import StateEmpty from 'app/KinerjaShared/components/StateEmpty'
import CardStateError from 'app/KinerjaShared/components/CardStateError'
import { useApiGetRiwayatHasilEvaluasi } from 'api/kinerja/kepala-sekolah/riwayat'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'

import type { TRiwayatId } from 'types/kinerja-riwayat-ks'

type EvaluationResultV2PeriodProps = {
  riwayatId?: TRiwayatId
  hasRootHeader?: boolean
}

const EvaluationResultV2Period: FC<EvaluationResultV2PeriodProps> = ({
  riwayatId,
  hasRootHeader = false,
}) => {
  const { getPeriod } = usePeriodContext()

  const {
    data: { data: result, meta } = { data: null, meta: null },
    isLoading: isHasilEvaluasiLoading,
    isSuccess: isHasilEvaluasiSuccess,
    isError: isHasilEvaluasiError,
  } = useApiGetRiwayatHasilEvaluasi(riwayatId, {
    enabled: !!riwayatId,
  })

  const isLoading = isHasilEvaluasiLoading

  const { periodLabel } = useMemo(() => {
    const periodYear = meta?.skpYear
    const periodNo = meta?.skpPeriod
    const periodData = getPeriod({ periodNo, periodYear })

    const periodBackupLabel =
      !!periodNo && !!periodYear
        ? `Periode ${periodNo} Tahun ${periodYear}`
        : ''

    const periodLabel = !!periodData
      ? `${formatPeriod(periodData?.startDate, periodData?.endDate, {
          separator: ' sampai dengan ',
          withDate: true,
          withYear: true,
        })}`
      : periodBackupLabel

    return { periodLabel }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result])

  // Define the data to be shown in the page
  const mappedResult = useMemo(() => {
    if (!result) return null

    return {
      details: [
        {
          label: 'Pegawai yang Dinilai',
          items: [
            { label: 'Nama', value: result?.ks?.nama },
            { label: 'NIP', value: result?.ks?.nip },
            { label: 'Jabatan', value: result?.ks?.jabatan },
            { label: 'Unit Kerja', value: result?.ks?.unitKerja },
          ],
        },
        {
          label: 'Pejabat Penilai Kinerja',
          items: [
            { label: 'Nama', value: result?.dinas?.nama },
            { label: 'NIP', value: result?.dinas?.nip },
            { label: 'Jabatan', value: result?.dinas?.jabatan },
            { label: 'Unit Kerja', value: result?.dinas?.unitKerja },
          ],
        },
        ,
      ],
      predicates: [
        {
          label: 'Praktik Kinerja',
          description:
            'Berdasarkan dokumen Tindak Lanjut dan Refleksi Tindak Lanjut.',
          value: result?.predikatKinerja?.praktikKinerja,
        },
        {
          label: 'Perilaku Kerja',
          description: 'Berdasarkan penilaian terhadap 7 aspek Perilaku Kerja.',
          value: result?.predikatKinerja?.perilakuKerja,
        },
      ],
      summary: {
        label: 'Predikat Kinerja*',
        value: result?.predikatKinerja?.predikatKinerja,
      },
    }
  }, [result])

  if (!riwayatId) return <StateEmpty />
  if (!isLoading && isHasilEvaluasiError) return <CardStateError />

  return (
    <>
      <EvaluationResult.Root>
        {hasRootHeader && (
          <EvaluationResult.Header title="Hasil Penilaian Kinerja Kepala Sekolah" />
        )}

        <EvaluationResult.Content>
          {isLoading && <EvaluationResult.Spinner />}

          {isHasilEvaluasiError && <EvaluationResult.Error />}

          {isHasilEvaluasiSuccess && (
            <EvaluationResult.Result.Root>
              {/* Holds the Header */}
              <EvaluationResult.Result.Header
                title="Hasil Penilaian Kinerja Kepala Sekolah"
                period={meta.skpPeriod}
              />

              <EvaluationResult.Result.Content>
                {/* Holds the "Nama Instansi" and "Periode Penilaian" */}
                <EvaluationResult.Result.Info
                  name={result?.namaInstansi}
                  customPeriod={<>{periodLabel}</>}
                />

                <Separator />

                {/* Holds the "Pegawai yang Dinilai" and "Pejabat Penilai Kinerja" */}
                <EvaluationResult.Result.Details data={mappedResult?.details} />

                {/* Holds the "Predikat Kinerja", "Poin Pengembangan Kompetensi", and footnote */}
                <EvaluationResult.Result.Summary
                  period={meta.skpPeriod}
                  data={{
                    badge: (
                      <Badge
                        variant="informational"
                        className={clsx({
                          '!bg-purple-20': meta.skpPeriod === 1,
                          '!bg-teal-20': meta.skpPeriod == 2,
                        })}
                      >
                        <CardPeriod
                          enableMultiplePeriodSupport
                          withYear={false}
                          period={meta?.skpPeriod}
                          year={meta?.skpYear}
                        />
                      </Badge>
                    ),
                    footnote: (
                      <Text
                        variant="body-sm-bold"
                        color="informational"
                        className=" mt-4"
                      >
                        *Predikat Kinerja untuk bulan{' '}
                        <CardPeriod
                          enableMultiplePeriodSupport
                          separator="-"
                          withYear={false}
                          period={meta?.skpPeriod}
                          year={meta?.skpYear}
                        />{' '}
                        hanya digunakan sebagai bahan diskusi bersama atasan
                        untuk peningkatan kinerja berikutnya.
                      </Text>
                    ),
                    predicates: mappedResult?.predicates,
                    summary: mappedResult?.summary,
                  }}
                />
              </EvaluationResult.Result.Content>
            </EvaluationResult.Result.Root>
          )}
        </EvaluationResult.Content>
      </EvaluationResult.Root>
    </>
  )
}
export default EvaluationResultV2Period
